'use client';

import { useState } from 'react';
import Link from 'next/link';
import { MdLocationOn } from 'react-icons/md';
import { FaCalendarAlt } from 'react-icons/fa';
import { BsChatDots } from 'react-icons/bs';
import { format } from 'date-fns';
import { buildSlugUrl } from '@/utils/slugUtils';

// Odd-shape thresholds: wider than 2.2:1 or taller than ~1:1.5 → show whole (contain).
// Everything in between (normal photos / flyers) fills the box (cover).
const WIDE = 2.2;
const TALL = 0.65;

const HomeCard = ({ type, data, isDemo = false }) => {
  // Per-image fit — declared before any early return (rules of hooks).
  const [fit, setFit] = useState('cover');
  const handleImageLoad = (e) => {
    const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
    if (!w || !h) return;
    const r = w / h;
    setFit(r > WIDE || r < TALL ? 'contain' : 'cover');
  };

  if (!data) return null;

  const FALLBACK_LISTING_IMAGE = process.env.NEXT_PUBLIC_FALLBACK_ADVERT_IMAGE_URL;
  const FALLBACK_EVENT_IMAGE = process.env.NEXT_PUBLIC_FALLBACK_EVENT_IMAGE_URL;

  // Shared image box: per-image cover/contain; odd shapes show whole on a soft
  // blurred backdrop instead of being cropped.
  const imageBox = (src, alt, badge = null) => (
    <div className="relative w-full h-40 sm:h-48 md:h-56 bg-slate-100 rounded-t-xl overflow-hidden">
      {badge}
      {fit === 'contain' && (
        <>
          <img
            src={src}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover scale-125 blur-2xl opacity-70"
          />
          <div className="absolute inset-0 bg-white/20" />
        </>
      )}
      <img
        src={src}
        alt={alt}
        onLoad={handleImageLoad}
        className={`relative w-full h-full ${fit === 'cover' ? 'object-cover' : 'object-contain'}`}
      />
    </div>
  );

  const renderContent = (showDemoBadge = false) => {
    const DemoBadge = () => (
      <span className="absolute top-2 right-2 md:right-2 px-2 py-0.5 bg-purple-600 text-white text-[10px] font-bold rounded z-10">
        DEMO
      </span>
    );

    switch (type) {
      case 'listing':
        return (
          <>
            {imageBox(data.image || FALLBACK_LISTING_IMAGE, data.title)}

            <div className="py-2 px-2">
              <h2 className="text-xs sm:text-sm text-center font-medium truncate text-slate-900">
                {data.title}
              </h2>

              <div className="flex items-center gap-1 truncate justify-center">
                <MdLocationOn className="text-xs text-slate-600 flex-shrink-0" />
                <span className="text-xs text-slate-600">
                  {data.suburb}, {data.state}
                </span>
              </div>
            </div>
          </>
        );

      case 'event':
        return (
          <>
            {imageBox(data.image || FALLBACK_EVENT_IMAGE, data.title, showDemoBadge ? <DemoBadge /> : null)}

            <div className="py-2 px-2">
              <h2 className="text-xs sm:text-sm text-center font-medium truncate text-slate-900">
                {data.title}
              </h2>

              <div className="flex items-center gap-1 truncate justify-center">
                <FaCalendarAlt className="text-xs text-slate-600 flex-shrink-0" />
                <span className="text-xs text-slate-600">
                  {data.date ? format(new Date(data.date), 'dd MMM yyyy') : 'Date TBA'}
                </span>
              </div>

              <div className="flex items-center gap-1 truncate justify-center">
                <MdLocationOn className="text-xs text-slate-600 flex-shrink-0" />
                <span className="text-xs text-slate-600">
                  {data.venue}
                </span>
              </div>
            </div>
          </>
        );

      case 'thread':
        return (
          <>
            <div className="relative w-full h-40 sm:h-48 md:h-56 bg-slate-100 rounded-t-xl flex items-center justify-center">
              <BsChatDots className="text-6xl text-teal-700 opacity-20" />
            </div>

            <div className="py-2 px-2">
              <h2 className="text-xs sm:text-sm text-center font-medium line-clamp-2 text-slate-900 min-h-[2.5rem]">
                {data.title}
              </h2>

              <div className="text-xs text-slate-600 text-center">
                by {data.author}
              </div>

              <div className="text-xs text-slate-600 text-center">
                {data.createdAt ? format(new Date(data.createdAt), 'dd MMM yyyy') : ''}
              </div>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  const linkHref = type === 'listing'
    ? buildSlugUrl('listing', data.title, data._id)
    : type === 'event'
    ? buildSlugUrl('event', data.title, data._id)
    : buildSlugUrl('thread', data.title, data._id);

  return (
    <Link href={linkHref} className="block w-full h-full">
      <div className="relative rounded-xl bg-white border border-slate-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 cursor-pointer transition-all duration-200 h-full overflow-hidden">
        {renderContent(isDemo)}
      </div>
    </Link>
  );
};

export default HomeCard;
